import React, { useLayoutEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Chip } from '@milanote/roma';
import classNames from 'classnames';

import Icon from '../../../components/icons/Icon';
import { useTranslator } from '../../../components/i18n/useTranslator';
import { holdFocusWithoutKeyboard } from '../../../mobile/boardsPage/editing/touchScreenFocusUtils';
import { SheetId } from '../../../mobile/structural/sheet/sheetTypes';
import { selectIsMobileAppMode } from '../../../platform/platformSelector';
import { userLocalePreferenceSelector, userTimeFormatPreferenceSelector } from '../../../user/currentUserSelector';
import { getDueDateClassFromData, getDueDatePillContentFromData } from '../../task/pills/assignment/assignmentUiUtils';
import { REMINDER_VALUES } from '../../task/pills/assignment/dueDate/taskDueDateConstants';
import '../../task/pills/assignment/dueDate/MobileDueDateSheet.scss';
import '../../task/pills/assignment/ChipPopover.scss';
import ToggleChip from '../behaviours/ToggleChip';
import ChipSelectionPopover from '../selectionViews/ChipSelectionPopover';
import ChipSelectionSheet from '../selectionViews/ChipSelectionSheet';
import type { ElementDueDateChipDescriptor } from '../elementChipsTypes';
import { ChipBaseProps } from '../../../../common/@roma/src/components/base/chip/ChipBase';
import getGridSize from '../../../utils/grid/gridSizeSelector';

type DueDateChipProps = {
    chip: ElementDueDateChipDescriptor;
    size: ChipBaseProps['size'];
    canModify: boolean;
    /** Removes the due date from the owning container. */
    onRemove: () => void;
    /** Renders the date editor shown in the chip's selection-owned popup. */
    renderPopupContent: (args: { closePopup: () => void }) => React.ReactNode;
};

type DueDateChipViewProps = ChipBaseProps & {
    dueDateText: React.ReactNode;
    containerId: string;
    chipRef: React.RefObject<HTMLSpanElement>;
    chipProps: React.HTMLAttributes<HTMLSpanElement> & {
        role: React.AriaRole;
        tabIndex: number;
        className: string;
    };
    isMobileAppMode: boolean;
    deselect: () => void;
    renderPopupContent: DueDateChipProps['renderPopupContent'];
    gridSize: number;
};

const getDueDateVariant = (dueDateClass: string, dueTimestamp: number | null): ChipBaseProps['variant'] => {
    if (!dueTimestamp || dueDateClass === 'completed') return 'normal';
    if (dueDateClass === 'overdue') return 'error';
    if (dueDateClass === 'due-today') return 'warning';

    return 'success';
};

// Matches the fixed .due-date-tool-popover width in TaskDueDatePopupContent.scss
const DUE_DATE_POPOVER_WIDTH_GU = 29;

/**
 * Centres the popover on the chip as it opens. The offset is measured once per
 * open so later chip width changes don't move the open popover.
 */
const usePopoverCrossOffset = ({
    gridSize,
    isOpen,
    chipRef,
}: {
    gridSize: number;
    isOpen?: boolean;
    chipRef: React.RefObject<HTMLElement>;
}): number => {
    const [crossOffset, setCrossOffset] = useState<number | null>(null);

    useLayoutEffect(() => {
        if (!isOpen) {
            setCrossOffset(null);
            return;
        }

        if (crossOffset !== null) return;

        const chipElement = chipRef.current;
        if (!chipElement) return;

        const chipWidth = chipElement.getBoundingClientRect().width;
        setCrossOffset((chipWidth - DUE_DATE_POPOVER_WIDTH_GU * gridSize) / 2);
    }, [isOpen, chipRef, gridSize]);

    return crossOffset ?? 0;
};

/**
 * Renders the due-date chip view with a popover anchor that holds the opening
 * chip width while selected.
 */
const DueDateChipView: React.FC<DueDateChipViewProps> = ({
    size,
    variant,
    className,
    leading,
    dueDateText,
    containerId,
    chipRef,
    chipProps,
    isSelected,
    isMobileAppMode,
    isDisabled,
    isSearchHighlighted,
    deselect,
    renderPopupContent,
    gridSize,
}) => {
    const crossOffset = usePopoverCrossOffset({ gridSize, isOpen: isSelected, chipRef });

    return (
        <>
            <Chip
                ref={chipRef}
                {...chipProps}
                size={size}
                variant={variant}
                className={classNames(chipProps.className, className)}
                leading={leading}
                isDisabled={isDisabled}
                isSearchHighlighted={isSearchHighlighted}
                isSelected={isSelected}
            >
                {dueDateText}
            </Chip>

            {isMobileAppMode ? (
                <ChipSelectionSheet
                    sheetId={SheetId.DueDateTool}
                    instanceId={containerId}
                    className="due-date-tool"
                    isOpen={!!isSelected}
                    onOpenChange={(isOpen) => !isOpen && deselect()}
                    onCloseTransitionStart={holdFocusWithoutKeyboard}
                >
                    {(close) => renderPopupContent({ closePopup: close })}
                </ChipSelectionSheet>
            ) : (
                <ChipSelectionPopover
                    triggerRef={chipRef}
                    isOpen={isSelected}
                    onOpenChange={(isOpen) => !isOpen && deselect()}
                    popoverClassName="due-date-tool-popover chip-popover"
                    placement="bottom start"
                    crossOffset={crossOffset}
                >
                    {(close) => renderPopupContent({ closePopup: close })}
                </ChipSelectionPopover>
            )}
        </>
    );
};

/**
 * Renders a due-date pill and its generic selection-owned editor view.
 */
const DueDateChip: React.FC<DueDateChipProps> = ({ chip, size, canModify, onRemove, renderPopupContent }) => {
    const translate = useTranslator('elements', 'elementTypes.task');
    const { containerId, dueTimestamp, hasDueDateTime, isComplete, dueReminder, isSearchHighlighted } = chip;
    const dueDateData = { dueTimestamp, hasDueDateTime, isComplete };
    const dueDateClass = getDueDateClassFromData(dueDateData);
    const hasReminder = dueReminder && dueReminder !== REMINDER_VALUES.NONE;
    const userLocale = useSelector(userLocalePreferenceSelector);
    const timeFormat = useSelector(userTimeFormatPreferenceSelector);
    const isMobileAppMode = useSelector(selectIsMobileAppMode);
    const gridSize = useSelector(getGridSize);

    return (
        <ToggleChip chip={chip} canModify={canModify} onDelete={onRemove}>
            {({ isSelected, canActivate, deselect, chipRef, chipProps }) => (
                <DueDateChipView
                    size={size}
                    variant={getDueDateVariant(dueDateClass, dueTimestamp)}
                    leading={hasReminder ? <Icon name="reminder-bell-mini" /> : undefined}
                    dueDateText={getDueDatePillContentFromData(dueDateData, translate, userLocale, timeFormat)}
                    containerId={containerId}
                    chipRef={chipRef}
                    chipProps={chipProps}
                    isSelected={isSelected}
                    isDisabled={!canActivate}
                    isSearchHighlighted={isSearchHighlighted}
                    isMobileAppMode={isMobileAppMode}
                    deselect={deselect}
                    renderPopupContent={renderPopupContent}
                    gridSize={gridSize}
                />
            )}
        </ToggleChip>
    );
};

export default DueDateChip;
